import React, { useEffect, useRef, useState } from 'react';
import { api, streamChat } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MiniMarkdown, Spinner } from '@/components/common';
import { toast } from 'sonner';
import { Send, Sparkles, Trash2, Bot, User } from 'lucide-react';
import { cn } from '@/lib/utils';

const QUICK_ACTIONS = [
  { label: 'What should I do next?', testId: 'ai-chat-quick-action-next' },
  { label: "Diagnose today's performance", testId: 'ai-chat-quick-action-diagnose' },
  { label: 'What creative should I make next?', testId: 'ai-chat-quick-action-creative' },
  { label: 'Should I kill or continue my current test?', testId: 'ai-chat-quick-action-kill' },
  { label: "What's blocking my products from scaling?", testId: 'ai-chat-quick-action-scale' },
];

export default function Assistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get('/ai/chat/history').then(({ data }) => { setMessages(data); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput('');
    setStreaming(true);
    setMessages((m) => [...m, { role: 'user', content: msg }, { role: 'assistant', content: '', streaming: true }]);
    try {
      await streamChat(msg, (delta) => {
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          copy[copy.length - 1] = { ...last, content: last.content + delta };
          return copy;
        });
      });
    } catch (e) {
      toast.error('AI chat failed — try again');
      setMessages((m) => m.slice(0, -1));
    } finally {
      setStreaming(false);
      setMessages((m) => m.map((x) => ({ ...x, streaming: false })));
    }
  };

  const clear = async () => {
    await api.delete('/ai/chat/history');
    setMessages([]);
    toast.success('Chat cleared');
  };

  return (
    <div data-testid="ai-chat-panel" className="mx-auto flex h-[calc(100vh-8.5rem)] max-w-3xl flex-col">
      <div className="flex items-center justify-between pb-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">AI Assistant</h1>
          <div className="page-accent-bar" />
          <p className="mt-2 text-sm text-muted-foreground">Your co-pilot. It sees your pipeline, metrics, creatives, tasks and time — and tells you what to do.</p>
        </div>
        {messages.length > 0 && (
          <Button data-testid="ai-chat-clear-button" variant="ghost" size="sm" onClick={clear} className="gap-1.5 text-muted-foreground">
            <Trash2 className="h-4 w-4" /> Clear
          </Button>
        )}
      </div>

      <div className="glass-data flex-1 space-y-4 overflow-y-auto rounded-2xl p-4">
        {!loaded ? (
          <div className="flex justify-center py-10"><Spinner className="h-6 w-6" /></div>
        ) : messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <p className="mt-3 text-sm font-medium">Ask anything about your operation</p>
            <p className="mt-1 max-w-sm text-xs text-muted-foreground">Direct, data-driven answers grounded in your actual products, metrics and creatives. If data is missing, it tells you what to log.</p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {QUICK_ACTIONS.map((q) => (
                <button key={q.label} data-testid={q.testId} onClick={() => send(q.label)}
                  className="glass-chip rounded-full px-3 py-1.5 text-xs hover:text-primary">
                  {q.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={m.id || `msg-${i}`} className={cn('flex gap-3', m.role === 'user' && 'justify-end')}>
              {m.role === 'assistant' && (
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10"><Bot className="h-4 w-4 text-primary" /></div>
              )}
              <div className={cn('max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm',
                m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted/60')}>
                {m.role === 'assistant' ? (
                  m.content ? <MiniMarkdown text={m.content} /> : <Spinner />
                ) : (
                  <p className="whitespace-pre-wrap">{m.content}</p>
                )}
              </div>
              {m.role === 'user' && (
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-secondary"><User className="h-4 w-4" /></div>
              )}
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {messages.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-2">
          {QUICK_ACTIONS.slice(0, 3).map((q) => (
            <button key={q.label} onClick={() => send(q.label)} disabled={streaming}
              className="glass-chip rounded-full px-2.5 py-1 text-[11px] text-muted-foreground hover:text-primary disabled:opacity-50">
              {q.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2 pt-2">
        <Textarea data-testid="ai-chat-input" value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="e.g. Is the neck massager worth scaling? Why is my CPA rising?" rows={2} className="resize-none" />
        <Button data-testid="ai-chat-send-button" onClick={() => send()} disabled={streaming || !input.trim()} size="icon" className="accent-btn h-[60px] w-12 shrink-0" aria-label="Send message">
          {streaming ? <Spinner /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}
